import { SvgIcon, Tooltip, Typography } from '@mui/material';
import { ParsedQuery } from 'query-string';
import { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AutocompleteOptions } from '@Components/Autocomplete';
import Button, { ButtonProps } from '@Components/Button';
import { TableData, TableRow } from '@Components/Table';
import { CommonConstants, Icons } from '@Constants/index';
import { VoiceProvider } from '@Models/callerIds.models';
import { CommonListResponse, MetaData, TimeFrame } from '@Models/common.models';
import { EmailsQuery } from '@Models/emails.models';
import { GlobalScriptsQuery, ScriptsQuery } from '@Models/scripts.models';
import { ConfirmationActionTypes, Icon } from '@Typings/global.types';

import query from './query.utils';

/**
 * Returns date-time string in MM-DD-YYYY HH24:MM format.
 * @param date Date Object
 * @param noTime (optional) boolean to exclude time from the date-time string
 * @returns date-time string in MM-DD-YYYY HH24:MM format
 */
export const getDateMMDDYYYYHHMM = (date: Date, noTime?: boolean): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${
    date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1
  }-${
    date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()
  }-${date.getFullYear()} ${
    !noTime
      ? `${hours < 10 ? `0${hours}` : hours}:${
          minutes < 10 ? `0${minutes}` : minutes
        }`
      : ''
  }`.trim();
};

/**
 * Returns date string in MM/DD/YYYY format.
 * @param date Date Object
 * @returns date string in MM/DD/YYYY format
 */
export const getDateMMDDYYYY = (date: Date): string =>
  `${
    date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1
  }/${
    date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()
  }/${date.getFullYear()}`.trim();

/**
 * Takes the passed parameters
 *
 * Returns data for table row in desired format.
 */
export const createData = (
  name: React.ReactNode,
  type: string,
  createdAt: string,
  groupName?: string,
  isPublic?: string,
  defaultType?: boolean,
  actions?: React.ReactNode
): TableData => {
  const defaultTypeString = defaultType?.toString() || '';

  return {
    name,
    ...(type && { type }),
    ...(groupName ? { groupName } : {}),
    ...(!type && { isPublic }),
    ...(defaultType !== undefined
      ? {
          defaultType:
            defaultTypeString?.charAt(0).toUpperCase() +
            defaultTypeString?.slice(1),
        }
      : {}),
    createdAt: getDateMMDDYYYYHHMM(new Date(createdAt)),
    actions,
  };
};

/**
 * Returns Rows for Table Component. (Scripts & Emails Module)
 * @param list - Response List
 * @param actions - Actions Menu Component
 * @param modalBtn - Modal Button Component to Open Script/Email
 * @param isAdmin - (optional) Boolean for Admin
 * @returns Returns tableRows
 */
export const GetRows = (
  list: CommonListResponse['list'] = [],
  actions: (
    id: string,
    disabled?: boolean,
    defaultType?: boolean
  ) => JSX.Element,
  modalBtn: (id: string, text: string) => JSX.Element,
  isAdmin?: boolean
): TableRow[] => {
  const { t } = useTranslation();
  /** Tooltip for Global Scripts */
  const globalTooltipText = t('common.tooltip.Global Script');

  const rows: TableRow[] = list.map((data): TableRow => {
    /** Tooltip for Default Scripts/Emails */
    const defaultTooltipText = t('common.tooltip.Default Checkbox', {
      type: data.subject ? t('Email.Email') : t('scripts.Script'),
    });

    /** Checkbox Element for Global Script */
    const globalCheckboxElement = data?.global ? (
      <Tooltip title={globalTooltipText} placement="top-start">
        <SvgIcon
          sx={{
            verticalAlign: 'middle',
            color: ({ colors }) => colors.iconGrey,
          }}
        >
          {Icons.public}
        </SvgIcon>
      </Tooltip>
    ) : undefined;

    /** Checkbox Element for Default Script/Email */
    const defaultCheckboxElement = data?.default ? (
      <Tooltip title={defaultTooltipText} placement="top-start">
        <SvgIcon
          sx={{
            color: 'action.disabledBackground',
            verticalAlign: 'middle',
          }}
        >
          {Icons.checkBoxBlank}
        </SvgIcon>
      </Tooltip>
    ) : undefined;

    return {
      id: data.id,
      data: createData(
        modalBtn(data.id, data.name),
        data.displayType,
        data.createdAt,
        isAdmin && data?.group !== undefined
          ? data?.group?.name ?? ' '
          : undefined,
        data.isPublic ? 'True' : 'False',
        !isAdmin ? data.default : undefined,
        actions(data.id, data.global, data.default)
      ),
      isNotCheckable: data?.global || data.default,
      checkboxElement: globalCheckboxElement || defaultCheckboxElement,
    };
  });

  return rows;
};

/**
 * Returns Pagination Select Options.
 */
export const getRowsPerPageOptions = (
  initialRowsPerPage: number,
  totalCount: number
): number[] => {
  const rowsPerPageOptions: number[] = [];
  for (
    let i = 0;
    i < 10 && i < Math.ceil(totalCount / initialRowsPerPage);
    i += 1
  ) {
    rowsPerPageOptions.push(initialRowsPerPage * (i + 1));
  }

  return rowsPerPageOptions;
};

/** Returns Filter Options to be selected initially. */
export const getSelectFilterValue = (
  params: string[]
): AutocompleteOptions[] => {
  const initialValue: AutocompleteOptions[] = params.map((param: string) => ({
    label: param,
    value: param,
  }));

  return initialValue;
};

/** Returns Stringified Date in given format(mm/dd/yyyy hh:mm) */
export const getDateString = (dateString: string): string =>
  dateString ? getDateMMDDYYYYHHMM(new Date(`${dateString}Z`)) : dateString;

/**
 * Add minutes to the passed date.
 * @param date - Date Object
 * @param minutes - Number of minutes to add
 * @returns new Date object with added minutes
 */
export const addMinutes = (date: Date, minutes: number): Date =>
  new Date(date.getTime() + minutes * 60000);

/**
 * Returns the passed date's `23.59.59.999` time as ISO string.
 * @param dateStr - Date String
 * @returns string
 */
export const nextDayISOString = (dateStr?: string): string =>
  dateStr ? addMinutes(new Date(dateStr), 1439.99999).toISOString() : '';

/** Returns Date Object/undefined from the provided ISO DateTime String. */
export const getDateFromISODateTimeString = (
  date?: string
): Date | undefined => {
  if (date) {
    return new Date(date);
  }

  return undefined;
};

/** Returns Select Component Options for Scripts & Emails (with translations) */
export const SelectOptions = (): AutocompleteOptions[] => {
  const { t } = useTranslation();

  const options: AutocompleteOptions[] = [
    {
      label: t('common.tableFilters.Expired'),
      value: 'Expired',
    },
    {
      label: t('common.tableFilters.FRBO'),
      value: 'FRBO',
    },
    {
      label: t('common.tableFilters.FSBO'),
      value: 'FSBO',
    },
    {
      label: t('common.tableFilters.Neighborhood'),
      value: 'Neighborhood',
    },
    {
      label: t('common.tableFilters.Preforeclosure'),
      value: 'Preforeclosure',
    },
    {
      label: t('common.tableFilters.Contact Plus'),
      value: 'Contact Plus',
    },
  ];

  return options;
};

/** Returns Select Reason Options for Global & Personal DNC (with translations) */
export const DNCSelectOptions = (): AutocompleteOptions[] => {
  const { t } = useTranslation();
  const options: AutocompleteOptions[] = [
    {
      label: t('common.tableFilters.Disconnected'),
      value: 'Disconnected',
    },
    {
      label: t('common.tableFilters.Litigator'),
      value: 'Litigator',
    },
  ];

  return options;
};

/** Returns Select Component Options for voice providers */
export const VoiceProviderSelectOptions = (): AutocompleteOptions[] => [
  {
    label: 'Auto',
    value: VoiceProvider.AUTO,
  },
  {
    label: 'Twilio',
    value: VoiceProvider.TWILIO,
  },
  {
    label: 'Vonage',
    value: VoiceProvider.VONAGE,
  },
  {
    label: 'Telnyx',
    value: VoiceProvider.TELNYX,
  },
];

/** Converts the given minutes to hours in the format of 'hh:mm:ss' */
export const minutesToHHMMSS = (minutes: string): string => {
  const min = parseFloat(minutes);
  if (Number.isNaN(min)) {
    return '00:00:00';
  }

  const hours = Math.floor(min / 60);
  const minutesLeft = min % 60;
  const seconds = Math.floor((minutesLeft % 1) * 60);
  return `${hours}:${minutesLeft < 10 ? `0${minutesLeft}` : minutesLeft}:${
    seconds < 10 ? `0${seconds}` : seconds
  }`;
};

/** Converts the given seconds to minutes in the format of 'mm:ss' */
export const secondsToMMSS = (seconds: string): string => {
  const sec = parseInt(seconds, 10);
  if (Number.isNaN(sec)) {
    return '00:00:00';
  }

  const minutes =
    Math.floor(sec / 60) < 10
      ? `0${Math.floor(sec / 60)}`
      : Math.floor(sec / 60);
  const secondsLeft = sec % 60;

  return `${minutes}:${secondsLeft < 10 ? `0${secondsLeft}` : secondsLeft}`;
};

/**
 * Download CSV file from stringified data
 * @param data CSV Data in form for string
 */
export const downloadCsv = (data: string, fileName: string): void => {
  const blob = new Blob([data], { type: 'data:text/csv;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  const currentDate = new Date().toLocaleDateString();
  link.download = `${fileName}__${currentDate}.csv`;
  link.click();
};

/** Return Count on non disabled rows */

/**
 * Takes Dnc Table and return count of non disabled rows
 * @param data Dnc Table
 * @returns count of non disabled rows
 */
export const getCheckableCount = (data?: TableRow[]): number =>
  data?.reduce((val: number, item) => {
    if (!item.disabled) return val + 1;
    return val;
  }, 0) || 0;

/**
 * Returns Sorting Icon to be used
 * @param direction - Direction of Sorting (ASC, DESC)
 * @returns string (Icon)
 */
export const getSortingIcon = (direction?: string): Icon => {
  switch (direction) {
    case CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC:
      return 'arrowDropUp';
    case CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.DESC:
      return 'arrowDropDown';
    default:
      return 'sorting';
  }
};

/** Converts the given seconds to hours in the format of 'hh:mm:ss' */
export const secondsToHHMMSS = (seconds: string): string => {
  const sec = parseInt(seconds, 10);
  if (Number.isNaN(sec)) {
    return '00:00:00';
  }

  const hh =
    Math.floor(sec / 3600) < 10
      ? `0${Math.floor(sec / 3600)}`
      : `${Math.floor(sec / 3600)}`;
  const mm =
    Math.floor((sec % 3600) / 60) < 10
      ? `0${Math.floor((sec % 3600) / 60)}`
      : `${Math.floor((sec % 3600) / 60)}`;
  const ss = sec % 60 < 10 ? `0${sec % 60}` : sec % 60;

  return `${hh}:${mm}:${ss}`;
};

/** Debounce Utility Function
 * @param func Function to be debounced
 * @param wait Time in milliseconds to wait before invoking the function
 */
export const debounce = <Params extends any[]>(
  func: (...args: Params) => any,
  timeout = CommonConstants.DEBOUNCE_TIMEOUT
): ((...args: Params) => void) => {
  let timer: ReturnType<typeof setTimeout>;

  return (...args: Params) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, timeout);
  };
};

/**
 * Returns the RTK Query according to given url queries
 * @param search - URL QUERY PARAMS
 * @returns RTK Query parameters
 */
export const getRTKQuery = (
  search: string
): ScriptsQuery | GlobalScriptsQuery | EmailsQuery => {
  const queries = query.parse(search);

  const dateStart = queries[
    CommonConstants.QUERY_PARAMS.COMMON.DATE_START
  ] as string;
  const dateEnd = queries[
    CommonConstants.QUERY_PARAMS.COMMON.DATE_END
  ] as string;

  return {
    pageNo:
      (queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO] as number) - 1,
    pageSize: queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE] as number,
    name: queries[CommonConstants.QUERY_PARAMS.COMMON.NAME] as string,
    order: queries[CommonConstants.QUERY_PARAMS.COMMON.ORDER] as string,
    type: queries[CommonConstants.QUERY_PARAMS.COMMON.TYPE] as string[],
    dateStart,
    dateEnd: nextDayISOString(dateEnd),
    userId: queries[CommonConstants.QUERY_PARAMS.USERS.USER_ID] as string,
    direction: queries[CommonConstants.QUERY_PARAMS.COMMON.DIRECTION] as string,
    hasUserTpr: queries[
      CommonConstants.QUERY_PARAMS.USERS.HAS_USER_TPR
    ] as string,
    hasUserLiveConnect: queries[
      CommonConstants.QUERY_PARAMS.USERS.HAS_USER_LIVE_CONNECT
    ] as string,
  };
};

/**
 * Returns the Modal Button for different modules(script/email/dnc) according to passed parameters
 * @param data - meta data for button component
 * @param id - Response id
 * @param name - Response name
 * @param onClick - handles click action on button
 * @param isDisabled - Condition for setting disabled state
 * @param isAdmin - Condition for setting globe icon in button
 * @returns Button JSX Element
 */
export const getModalBtnComponent = (data: {
  id: string;
  name: string;
  onClick?: ButtonProps['onClick'];
  isDisabled?: boolean;
  isAdmin?: boolean;
  hideEndIcon?: boolean;
}): JSX.Element => (
  <Button
    variant="text"
    id={data.id}
    color="secondary"
    text={data.name}
    endIcon={data.isAdmin && !data.hideEndIcon ? Icons.public : null}
    disabled={data.isDisabled}
    sx={{
      justifyContent: 'flex-start',
      minWidth: 0,
      minHeight: 0,
      p: 0.2,
      fontWeight: 'normal',
      textTransform: 'none',
    }}
    onClick={data.onClick}
  />
);

/**
 * @description Coverts details from url to object
 * @param queries Parameters from URL
 * @returns Object to be sent as payload for API call
 */
export const getFilters = (
  queries: ParsedQuery<string | number>,
  isArchived?: boolean
): MetaData => {
  const startDate = new Date(
    queries[CommonConstants.QUERY_PARAMS.COMMON.DATE_START] as string
  );
  const endDate = new Date(
    queries[CommonConstants.QUERY_PARAMS.COMMON.DATE_END] as string
  );
  const orderField = queries[
    CommonConstants.QUERY_PARAMS.COMMON.ORDER
  ] as string;
  const orderDirection = queries[
    CommonConstants.QUERY_PARAMS.COMMON.DIRECTION
  ] as string;

  return {
    filters: {
      dateStart: startDate,
      dateEnd: endDate,
      ...(isArchived ? { archive: '' } : {}),
    },
    order: {
      field: orderField,
      direction: orderDirection,
    },
  };
};

/**
 * Returns the response data with the given id
 * @param list array of response data with multiple ids
 * @param matchString id to be searched
 * @returns data according to id
 */
export const getResponseData = (
  list?: CommonListResponse['list'],
  id = ''
): CommonListResponse['list'][0] | undefined =>
  list?.filter((item) => item.id === id)[0];

/**
 * Initiates the download of the PDF
 * @param base64Data base64 data string which is converted to PDF
 */
export const downloadPDF = (data: string, name: string): void => {
  const link = document.createElement('a');
  const blob = new Blob([data], { type: 'application/pdf' });
  link.href = URL.createObjectURL(blob);
  const currentDate = new Date().toLocaleDateString();
  link.download = `${name}__${currentDate}.pdf`;
  link.click();
};

/**
 * Returns confirmation message translation key according to action type
 * @param confirmationActionType - ConfirmationActionTypes
 * @param specialMessage (optional) - Boolean flag if msg is special
 * @returns string - translation key
 */
export const getConfirmationMessageText = (
  confirmationActionType: ConfirmationActionTypes | null,
  specialMessage?: boolean
): string => {
  switch (confirmationActionType) {
    case ConfirmationActionTypes.Save:
      return 'common.confirmationMessages.Default Data Overwrite';
    case ConfirmationActionTypes.Delete:
      return 'common.confirmationMessages.Delete Confirmation';
    case ConfirmationActionTypes.Archive:
      return specialMessage
        ? 'common.confirmationMessages.Default Data Archive Confirmation'
        : 'common.confirmationMessages.Archive Confirmation';
    case ConfirmationActionTypes.Unarchive:
      return 'common.confirmationMessages.Unarchive Confirmation';
    case ConfirmationActionTypes.Restore:
      return 'common.confirmationMessages.Restore Confirmation';
    default:
      return 'common.confirmationMessages.Restore Confirmation';
  }
};

/**
 * Returns the Confirmation Message Node
 * @param confirmationActionType - ConfirmationActionTypes
 * @param defaultType (optional) - Boolean flag for `default` key value
 * @param listType (optional) - List Type to show in confirmation message (FSBO, FRBO, etc.)
 * @returns ReactNode - Confirmation Message with proper translations
 */
export const getConfirmationMessageNode = (
  confirmationActionType: ConfirmationActionTypes | null,
  defaultType?: boolean,
  listType = ''
): ReactNode => (
  <Typography>
    <Trans
      i18nKey={getConfirmationMessageText(confirmationActionType, defaultType)}
      components={{ strong: <strong /> }}
      values={{ listType }}
    />
  </Typography>
);

/**
 * @description To Format 10 digit Number in US Format
 * @param phoneNumberString 10 digit phone number
 * @returns US Format phone number
 */
export const formatToUSNumber = (phoneNumberString: string): string => {
  const phoneDigits = `${phoneNumberString}`.replace(/\D/g, '');
  const phoneGroups = /^(1|)?(\d{3})(\d{3})(\d{4})$/.exec(phoneDigits) || [];
  const intlCode = phoneGroups[1] ? '+1 ' : '';
  if (phoneGroups) {
    return [
      intlCode,
      '(',
      phoneGroups[2],
      ') ',
      phoneGroups[3],
      '-',
      phoneGroups[4],
    ].join('');
  }
  return '';
};

/**
 * @description To Format 11 digit Number in UA Format
 * @param phoneNumberString 11 digit phone number
 * @returns UA Format phone number
 */
export const formatToUANumber = (phoneNumberString: string): string => {
  const phoneDigits = `${phoneNumberString}`.replace(/\D/g, '');
  const phoneGroups = /^(38|)?(\d{3})(\d{3})(\d{4})$/.exec(phoneDigits) || [];
  const intlCode = phoneGroups[1] ? '+38 ' : '';
  if (phoneGroups) {
    return [
      intlCode,
      '(',
      phoneGroups[2],
      ') ',
      phoneGroups[3],
      '-',
      phoneGroups[4],
    ].join('');
  }
  return '';
};

export const formatNumber = (phoneNumberString: string): string =>
  phoneNumberString.startsWith('+380')
    ? formatToUANumber(phoneNumberString)
    : formatToUSNumber(phoneNumberString);

/**
 * @description To Format US Number in to simple 10 digit
 * This will remove white-spaces, parenthesis, hyphens
 * @param phoneNumberString US Format phone number
 * @returns 10 digit phone number
 */
export const unformatUSNumber = (phoneNumberString: string): string =>
  phoneNumberString.replace(/[\s]|[-]|[(]|[)]/g, '');

export const unformatNumber = (phone: string): string => {
  const filteredDigits = phone.replace(/\D/g, '');
  let planePhone: string;
  if (filteredDigits.startsWith('0')) {
    planePhone = `+38${unformatUSNumber(phone)}`;
  } else {
    planePhone = `+1${unformatUSNumber(phone)}`;
  }
  return planePhone;
};

/**
 * @description To parse time frame value when calls are allowed
 * @param featureValue input string
 * @returns startTimeParsed and endTimeParsed values
 */
export const getCallTimeFrameFromFeatureValue = (
  featureValue: string
): TimeFrame => {
  const [startTimeParsed, endTimeParsed] = featureValue
    .split('-')
    .map((str) => str.trim());
  return {
    startTimeParsed,
    endTimeParsed,
  };
};
